/* global functionality */
import React, { useState, useEffect, useMemo, useContext } from "react";
import styled from "styled-components";
import Request from "common/Request";
import { expressTarget, safeStr } from "common/api";
import colors from "common/graphs/colors";
import { useColorMap } from "./livemetrics.utils";
import useProfile from "common/hooks/useProfile";
import BqnContext from "common/BqnContext";
import Table from "common/graphs/Table/Table";
import {
  ColorLabelNumberCell,
  ColorLabelCell,
  NumberCell,
} from "common/graphs/Table/TableRenders";
import { atoiv6, atoiv4, durationToInt } from "common/utils";

const HIGHLIGHT_COLOR = "#3d85c6";

const TableContainer = styled.div`
  width: 100%;
  max-width: 100%;
  overflow: auto;

  @media print {
    width: 40cm;
    transform: scale(0.5, 0.5);
    overflow-x: hidden;
    margin-left: -400px;
    margin-top: -350px;
    .dataTables_length,
    .dataTables_filter {
      display: none;
    }
  }

  table {
    width: 100% !important;
    max-width: 100% !important;
  }

  &.liveMode {
    .dataTables_length,
    .dataTables_filter > label {
      display: none;
    }

    table.dataTable thead {
      .sorting_desc,
      .sorting_asc,
      .sorting {
        background-image: none !important;
      }
    }

    table.dataTable thead th {
      cursor: default;
    }
  }

  .domain-column {
    text-overflow: ellipsis;
    width: 250px !important;
    max-width: 250px;
    overflow: hidden;
  }
`;

const HeaderWithControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;

  h4 {
    margin-top: 0px !important;
  }
`;

export const SubscriberFlowsResult = ({
  response,
  liveMode,
  setLiveMode,
  pageLength = 50,
  dpiFields,
  target,
  dpiEnabled,
}) => {
  const profile = useProfile() || {};
  const [colorMap, setColorMap] = useColorMap({
    response,
    liveMode,
    dpiFields,
  });

  const {
    clusterStatus: { mode },
  } = useContext(BqnContext);

  const [header, ...rows] = response.trim().split("\n");
  const headerAttrs = header.trim().split(/\s+/);
  const newRows = rows.map((row) => {
    const rowAttrs = row.trim(/\s/).split(/\s+/);
    const newEntry = rowAttrs.reduce((acc, attr, index) => {
      acc[headerAttrs[index]] = attr;
      return acc;
    }, {});
    return newEntry;
  });

  const columns = useMemo(() => {
    return [
      ...(mode !== "standalone"
        ? [
            {
              label: "BQN",
              id: "BQN",
              numeric: true,
            },
          ]
        : []),
      ...(profile.dpiEnabled === true
        ? [
            {
              label: "DPI",
              id: "DPI",
              numeric: false,
              renderCell: (value) => {
                const color = colorMap?.[value] || colors[0];
                return <ColorLabelCell value={value} color={color} />;
              },
            },
            {
              label: "DOMAIN",
              id: "DOMAIN",
              numeric: false,
              cellClassName: "domain-column",
            },
            {
              label: "FLOW-POLICY",
              id: "RATE-POLICY",
              numeric: false,
              cellClassName: profile.hideRates ? null : "hyperlink-text",
              onClick: profile.hideRates
                ? null
                : (value) => {
                    setLiveMode(false);
                    globalNavigate("viewPolicyFlowEdit", {
                      ...target,
                      returnView: "viewStatusSubscribers",
                      policy: value,
                      dpiEnabled,
                    });
                  },
            },
          ]
        : []),
      {
        label: "MBPS-DN",
        id: "MBPS-DN",
        numeric: true,
        renderCell: (value) => {
          const color = value > 0 ? HIGHLIGHT_COLOR : "transparent";
          return (
            <ColorLabelNumberCell
              value={value}
              color={color}
              precision="fourDecimals"
            />
          );
        },
      },
      {
        label: "MBPS-UP",
        id: "MBPS-UP",
        numeric: true,
        renderCell: (value) => {
          const color = value > 0 ? HIGHLIGHT_COLOR : "transparent";
          return (
            <ColorLabelNumberCell
              value={value}
              color={color}
              precision="fourDecimals"
            />
          );
        },
      },
      {
        label: "MBYTES-DN",
        id: "MBYTES-DN",
        numeric: true,
        renderCell: (value) => (
          <NumberCell precision="fourDecimals" value={value} />
        ),
      },
      {
        label: "MBYTES-UP",
        id: "MBYTES-UP",
        numeric: true,
        renderCell: (value) => (
          <NumberCell precision="fourDecimals" value={value} />
        ),
      },
      {
        label: "PROTO",
        id: "PROTO",
        numeric: false,
      },
      {
        label: "AC-ADDR",
        id: "ACC-ADDR",
        numeric: false,
      },
      {
        label: "APORT",
        id: "APORT",
        numeric: true,
        renderCell: (value) => (
          <NumberCell value={value} hasNA={true} thousands={false} />
        ),
      },
      {
        label: "INET-ADDR",
        id: "INET-ADDR",
        numeric: false,
        parse: (value) => {
          if (!value) {
            return 0;
          }
          return value.includes(":") ? atoiv6(value) : atoiv4(value);
        },
        parseFilter: (value)=> value
      },
      {
        label: "IPORT",
        id: "IPORT",
        numeric: true,
        renderCell: (value) => (
          <NumberCell value={value} hasNA={true} thousands={false} />
        ),
      },
      {
        label: "ST",
        id: "ST",
        numeric: false,
      },
      {
        label: "LIFETIME",
        id: "LIFETIME",
        numeric: false,
        parse: (data) => (data === "n/a" || !data ? 0 : durationToInt(data)),
      },
    ];
  }, [JSON.stringify(colorMap), JSON.stringify(profile)]);

  return (
    <TableContainer
      className={
        liveMode ? "liveMode subscriberflows-table" : "subscriberflows-table"
      }
    >
      <Table
        isSelectable={false}
        isRowSelectable={false}
        data={newRows}
        sort={true}
        columns={columns}
        search={true}
        csvPrefix="subscribers-active-flows"
        rowsPerPageNr={50}
      />
    </TableContainer>
  );
};

const HeaderDiv = styled.div`
  display: flex;
  padding: 0 !important;
  &.header {
    border-color: transparent;
  }
  & .title {
    flex: 100% 1 1;
    padding-right: 2.5ch;
    text-align: center;
  }
`;

const doesNothing = () => {};
const SubscriberFlows = ({
  target,
  liveMode,
  title,
  setLiveMode,
  dpiEnabled,
}) => {
  const [request, setRequest] = useState(null);

  const {
    bqnId,
    clusterStatus: { mode },
  } = useContext(BqnContext);

  const doLoad = () => {
    setRequest(doRequest({ target }));
  };

  const doRequest = ({ target }) =>
    expressTarget(target).then((expressedTarget) =>
      ifCl
        .run(
          `show flow all ${expressedTarget} speed${
            mode === "cluster" && bqnId !== "all" ? ` bqn-id ${bqnId}` : ""
          }`
        )
        .then((response) => ({ response }))
    );

  const targetStr = target ? (target.addr ? target.addr : target.subsId) : "";

  useEffect(() => doLoad(), [targetStr, bqnId]);

  const ContextMenu = ({ doLoad = doesNothing }) => {
    return (
      <ul className="context-menu header-dropdown">
        <li>
          <a
            onClick={doLoad}
            data-toggle="cardloading"
            data-loading-effect="pulse"
            title="Refresh"
          >
            <i className="material-icons">refresh</i>
          </a>
        </li>
      </ul>
    );
  };

  const headerClassName = "header subscriber-flows-header";

  return (
    <>
      <HeaderWithControls className={headerClassName}>
        <h4 className="title subtitle margin-0">{title ?? "Active Flows"}</h4>
        <ContextMenu doLoad={doLoad} />
      </HeaderWithControls>
      {request === null ? null : (
        <Request during={request}>
          {(result) => (
            <SubscriberFlowsResult
              {...result}
              liveMode={liveMode}
              setLiveMode={setLiveMode}
              target={target}
              dpiEnabled={dpiEnabled}
            />
          )}
        </Request>
      )}
    </>
  );
};

export default SubscriberFlows;
